/* Ayudate · Panel de administración
   Cola de aprobación de tutores y cursos pendientes (ver apps-script.gs,
   sección 10). Una sola contraseña compartida por todo el equipo — no hay
   usuario por persona, ver CONFIG.claveAdminHash — así que no hace falta
   más que un login simple con esa contraseña; el servidor es quien manda
   sobre qué sesión sigue viva (ver validarSesionAdmin). Aprobar/rechazar
   acá hace exactamente lo mismo que hoy hace un admin editando la hoja
   "Tutores" a mano (estado + publicado), solo que desde el navegador. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api, ErrorApi } from "../api";
import { irA_ruta } from "../lib/rutas";
import { guardarSesionAdmin, leerSesionAdmin, borrarSesionAdmin } from "../lib/sesionAdmin";
import { precioDe } from "../lib/tarifas";
import { colones } from "../lib/formato";
import { expandirOfertas } from "../lib/horarios";
import { AREAS, infoCurso } from "../datos/areas";
import { Capa } from "../componentes/Capa";
import { AlertaArchivo } from "../componentes/Mensajes";
import { prepararComprobante } from "../lib/comprobante";
import { validarExtensionImagen, FORMATOS_IMAGEN_TEXTO } from "../lib/tiposArchivo";
import { Accesibilidad, FiltrosColor } from "../componentes/Accesibilidad";
import { cargarA11y, guardarA11y, clasesA11y } from "../lib/a11y";
import { NOMBRE_TIPO_CUENTA } from "../datos/cuentasBancarias";

function fecha(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-CR", { day: "numeric", month: "long", year: "numeric" });
}

/* El ciclo llega como "AAAA-MM-DD" del viernes en que arranca (ver
   inicioSemanaISO_ en apps-script.gs: el ciclo de pago corre de viernes
   00:00 a jueves 23:59) — acá solo se muestra como rango legible. */
function rangoSemana(semanaISO) {
  const inicio = new Date(`${semanaISO}T00:00:00`);
  if (Number.isNaN(inicio.getTime())) return semanaISO;
  const fin = new Date(inicio);
  fin.setDate(fin.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("es-CR", { day: "numeric", month: "short" });
  return `${fmt(inicio)} – ${fmt(fin)}`;
}

/* Exportación CSV: sin librería, alcanza con escapar comillas/comas y
   anteponer el BOM de UTF-8 para que Excel no rompa las tildes/ñ. */
function aCSV(filas, columnas) {
  const escapar = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const encabezado = columnas.map((c) => escapar(c.etiqueta)).join(",");
  const cuerpo = filas.map((f) => columnas.map((c) => escapar(c.valor(f))).join(",")).join("\n");
  return `${encabezado}\n${cuerpo}`;
}

/* Mismo cálculo que inicioSemanaISO_() en apps-script.gs: el ciclo de pago
   corre de viernes 00:00 a jueves 23:59, así que la clave de "esta semana"
   es la fecha del viernes en que arrancó el ciclo activo. Se usa para
   filtrar sesionesFinancieras (ya viene sin agrupar, con su propio campo
   "semana") y armar las métricas semanales de arriba sin pedirle nada
   nuevo al servidor. */
function inicioSemanaISO(f) {
  const d = new Date(f.getFullYear(), f.getMonth(), f.getDate());
  const dia = d.getDay();
  const diasDesdeElViernes = (dia + 2) % 7;
  d.setDate(d.getDate() - diasDesdeElViernes);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function descargarCSV(nombreArchivo, csv) {
  const BOM = String.fromCharCode(0xFEFF);   // para que Excel detecte UTF-8 y no rompa tildes/ñ
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function PaginaAdmin() {
  const [sesion, setSesion] = useState(() => leerSesionAdmin());
  const [password, setPassword] = useState("");
  const [entrando, setEntrando] = useState(false);
  const [errorLogin, setErrorLogin] = useState("");

  const [cargando, setCargando] = useState(true);
  const [pendientes, setPendientes] = useState(null);
  const [error, setError] = useState("");
  const [exito, setExito] = useState("");
  const [procesando, setProcesando] = useState(null);   // { idTutor, curso, accion } | null
  const exitoTimeoutRef = useRef(null);
  const [a11y, setA11y] = useState(cargarA11y);
  useEffect(() => { guardarA11y(a11y); }, [a11y]);

  const mostrarExito = useCallback((mensaje) => {
    if (exitoTimeoutRef.current) clearTimeout(exitoTimeoutRef.current);
    setExito(mensaje);
    exitoTimeoutRef.current = setTimeout(() => setExito(""), 4000);
  }, []);

  const salir = useCallback(() => {
    borrarSesionAdmin();
    setSesion(null);
    setPendientes(null);
  }, []);

  /* Accesos rápidos de la barra superior: desplazamiento suave a la
     sección, respetando prefers-reduced-motion — mismo criterio que
     SitioPublico.jsx (irA), reimplementado acá porque no hay un helper
     compartido de scroll-a-ancla entre páginas. Si la sección todavía no
     está en el DOM (p. ej. "Cursos adicionales" mientras cargando === true)
     el clic simplemente no hace nada. */
  const irA = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const suave = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: suave ? "smooth" : "auto", block: "start" });
  };

  const cargar = useCallback(async (token) => {
    setCargando(true); setError("");
    try {
      const r = await api.obtenerColaAprobacion(token);
      setPendientes(r.pendientes);
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      setError(err.message || "No pudimos cargar la cola de aprobación.");
    } finally {
      setCargando(false);
    }
  }, [salir]);

  useEffect(() => { if (sesion) cargar(sesion); }, [sesion, cargar]);

  /* ----------------------------------------------------------------
     MÉTRICAS GLOBALES Y RANKING DE TUTORES
     Se arman con los mismos datos públicos que ya usa el buscador
     (api.getTutores + expandirOfertas): una fila por curso publicado
     y aprobado, con sus sesiones, calificación y tarifas reales.
     No hace falta un endpoint aparte en el backend.
  ---------------------------------------------------------------- */
  const [tutoresPublicos, setTutoresPublicos] = useState(null);
  const [filtroArea, setFiltroArea] = useState("todas");
  const [filtroCurso, setFiltroCurso] = useState("todos");

  useEffect(() => {
    if (!sesion) return;
    api.getTutores().then(setTutoresPublicos).catch(() => setTutoresPublicos([]));
  }, [sesion]);

  /* Puntualidad de cada tutor (a tiempo vs. tardías), a partir de las
     sesiones ya calificadas — ver obtenerPuntualidadAdmin en
     apps-script.gs. Es un rasgo del tutor, no de una oferta puntual: se
     indexa por idTutor solamente. */
  const [puntualidad, setPuntualidad] = useState(null);

  useEffect(() => {
    if (!sesion) return;
    api.obtenerPuntualidadAdmin(sesion)
      .then((r) => setPuntualidad(r.puntualidad))
      .catch((err) => {
        if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
        setPuntualidad([]);
      });
  }, [sesion, salir]);

  const puntualidadPorTutor = useMemo(() => Object.fromEntries(
    (puntualidad || []).map((p) => [p.idTutor, p])
  ), [puntualidad]);

  /* "92% a tiempo · 1 tardía registrada" — null cuando ese tutor todavía
     no tiene ninguna sesión calificada con el dato de puntualidad. */
  const textoPuntualidad = (idTutor) => {
    const p = puntualidadPorTutor[idTutor];
    if (!p || p.total === 0) return null;
    const tardias = p.tardias === 1 ? "1 tardía registrada" : `${p.tardias} tardías registradas`;
    return `${p.porcentaje}% a tiempo · ${tardias}`;
  };

  useEffect(() => { setFiltroCurso("todos"); }, [filtroArea]);

  const ofertas = useMemo(() => expandirOfertas(tutoresPublicos || []), [tutoresPublicos]);

  const metricas = useMemo(() => {
    const totalSesiones = ofertas.reduce((s, o) => s + o.sesiones, 0);
    const ingresos = ofertas.reduce((s, o) => s + o.sesiones * o.tarifaCliente, 0);
    const comision = ofertas.reduce((s, o) => s + o.sesiones * (o.tarifaCliente - o.tarifaTutor), 0);
    const porCurso = {};
    ofertas.forEach((o) => { porCurso[o.curso] = (porCurso[o.curso] || 0) + o.sesiones; });
    const [cursoTop, sesionesTop] =
      Object.entries(porCurso).sort((a, b) => b[1] - a[1])[0] || [null, 0];
    return { totalSesiones, ingresos, comision, cursoTop, sesionesTop };
  }, [ofertas]);

  const areasPresentes = useMemo(() => {
    const ids = new Set(ofertas.map((o) => infoCurso(o.curso).area));
    return AREAS.filter((a) => ids.has(a.id));
  }, [ofertas]);

  const cursosPresentes = useMemo(() => {
    const base = filtroArea === "todas"
      ? ofertas : ofertas.filter((o) => infoCurso(o.curso).area === filtroArea);
    const vistos = new Set();
    const lista = [];
    base.forEach((o) => {
      if (vistos.has(o.curso)) return;
      vistos.add(o.curso);
      lista.push({ curso: o.curso, nombre: infoCurso(o.curso).nom });
    });
    return lista.sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [ofertas, filtroArea]);

  const ranking = useMemo(() => ofertas
    .filter((o) => filtroArea === "todas" || infoCurso(o.curso).area === filtroArea)
    .filter((o) => filtroCurso === "todos" || o.curso === filtroCurso)
    .slice()
    .sort((a, b) => b.sesiones - a.sesiones || (b.rating ?? 0) - (a.rating ?? 0)),
  [ofertas, filtroArea, filtroCurso]);

  /* Paginación estricta: 10 tutores por página. La posición mostrada en
     "#" sigue el orden real del ranking (la página 2 arranca en el 11),
     no se reinicia en cada página. */
  const POR_PAGINA_RANKING = 10;
  const [paginaRanking, setPaginaRanking] = useState(1);
  useEffect(() => { setPaginaRanking(1); }, [filtroArea, filtroCurso]);
  const paginasRanking = Math.max(1, Math.ceil(ranking.length / POR_PAGINA_RANKING));
  const paginaRankingSegura = Math.min(paginaRanking, paginasRanking);
  const rankingVisible = ranking.slice(
    (paginaRankingSegura - 1) * POR_PAGINA_RANKING,
    paginaRankingSegura * POR_PAGINA_RANKING
  );

  /* ----------------------------------------------------------------
     FICHA DETALLADA DE UN TUTOR
     Se abre al hacer clic en un tutor del ranking; agrega TODOS sus
     cursos (no solo el de la fila clickeada) con los mismos datos
     públicos ya cargados arriba — sin pedir nada nuevo al servidor.
  ---------------------------------------------------------------- */
  const [tutorSeleccionado, setTutorSeleccionado] = useState(null);   // idTutor | null

  useEffect(() => {
    if (!tutorSeleccionado) return;
    const h = (e) => e.key === "Escape" && setTutorSeleccionado(null);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [tutorSeleccionado]);

  const perfilTutor = useMemo(() => {
    if (!tutorSeleccionado) return null;
    const cursos = ofertas.filter((o) => o.id === tutorSeleccionado);
    if (!cursos.length) return null;
    return {
      nombre: cursos[0].nom,
      carrera: cursos[0].carrera,
      anio: cursos[0].anio,
      totalSesiones: cursos.reduce((s, c) => s + c.sesiones, 0),
      ingresos: cursos.reduce((s, c) => s + c.sesiones * c.tarifaCliente, 0),
      ganancias: cursos.reduce((s, c) => s + c.sesiones * c.tarifaTutor, 0),
      puntualidad: puntualidadPorTutor[tutorSeleccionado] || null,
      cursos,
    };
  }, [tutorSeleccionado, ofertas, puntualidadPorTutor]);

  /* ----------------------------------------------------------------
     FINANZAS Y LIQUIDACIONES
     Desglose semanal real de lo cobrado por SINPE (no viene de los datos
     públicos de arriba: hace falta un endpoint propio que lea
     "Solicitudes" — ver listarFinanzasAdmin en apps-script.gs, sección 11).
  ---------------------------------------------------------------- */
  const [finanzas, setFinanzas] = useState(null);   // null = cargando
  const [sesionesFinancieras, setSesionesFinancieras] = useState(null);   // detalle sin agrupar, para el CSV
  const [errorFinanzas, setErrorFinanzas] = useState("");
  const [liquidando, setLiquidando] = useState(null);   // "semana::idTutor" en curso | null
  /* Ciclo esperando su comprobante de pago antes de poder marcarse PAGADO
     (ver ModalComprobantePago más abajo) — la fila de "finanzas" misma, o
     null si el modal está cerrado. Deshacer un PAGADO ya marcado (el botón
     "✓ Pagado") no pasa por acá: solo MARCAR exige la imagen. */
  const [pagoParaComprobante, setPagoParaComprobante] = useState(null);
  const [buscarFinanzas, setBuscarFinanzas] = useState("");
  /* Por defecto se abre mostrando solo lo pendiente de pagar: es la
     acción que de verdad hay que tomar acá, y con "Todos" de entrada la
     fila que importa queda enterrada entre ciclos ya liquidados. */
  const [filtroPagoFinanzas, setFiltroPagoFinanzas] = useState("pendientes");

  useEffect(() => {
    if (!sesion) return;
    api.obtenerFinanzasAdmin(sesion)
      .then((r) => { setFinanzas(r.semanas); setSesionesFinancieras(r.sesiones); })
      .catch((err) => {
        if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
        setErrorFinanzas(err.message || "No pudimos cargar las finanzas.");
        setFinanzas([]);
        setSesionesFinancieras([]);
      });
  }, [sesion, salir]);

  /* Solo deshace un pago ya marcado (nunca lo marca: eso pasa por
     ModalComprobantePago más abajo, que exige el comprobante primero) —
     por eso nunca manda `comprobante`, el servidor no lo pide para
     desmarcar. */
  const alternarLiquidacion = async (fila) => {
    const clave = `${fila.semana}::${fila.tutorId}`;
    if (liquidando) return;
    setLiquidando(clave);
    setErrorFinanzas("");
    try {
      await api.marcarLiquidacionAdmin(sesion, fila.semana, fila.tutorId, fila.tutorNombre, false);
      setFinanzas((prev) => prev.map((f) =>
        (f.semana === fila.semana && f.tutorId === fila.tutorId ? { ...f, liquidado: false } : f)));
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      setErrorFinanzas(err.message || "No pudimos actualizar esa liquidación.");
    } finally {
      setLiquidando(null);
    }
  };

  /* Marca un ciclo como PAGADO con el comprobante ya elegido (ver
     ModalComprobantePago): a diferencia de alternarLiquidacion() de
     arriba, este SIEMPRE va hacia "pagado" y siempre manda el
     comprobante — el servidor lo exige para esa dirección. El error se
     relanza para que el modal lo muestre sobre el propio formulario en
     vez de perderse detrás de él. */
  const marcarComoPagado = async (fila, comprobante) => {
    setLiquidando(`${fila.semana}::${fila.tutorId}`);
    try {
      const r = await api.marcarLiquidacionAdmin(
        sesion, fila.semana, fila.tutorId, fila.tutorNombre, true, comprobante);
      setFinanzas((prev) => prev.map((f) => (f.semana === fila.semana && f.tutorId === fila.tutorId
        ? { ...f, liquidado: true, comprobante: r.comprobante || f.comprobante }
        : f)));
      setPagoParaComprobante(null);
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      throw err;
    } finally {
      setLiquidando(null);
    }
  };

  /* Búsqueda por nombre de tutor + filtro por estado de pago, sobre la
     tabla de finanzas — mismo criterio de "filtros avanzados" que ya
     tiene el ranking de arriba (área/curso), aplicado acá al listado que
     de verdad mueve plata. */
  const finanzasFiltradas = useMemo(() => {
    const q = buscarFinanzas.trim().toLowerCase();
    return (finanzas || []).filter((f) => {
      if (filtroPagoFinanzas === "pendientes" && f.liquidado) return false;
      if (filtroPagoFinanzas === "pagados" && !f.liquidado) return false;
      if (q && !f.tutorNombre.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [finanzas, buscarFinanzas, filtroPagoFinanzas]);

  /* Paginación estricta: 10 ciclos por página, mismo criterio que el
     ranking de arriba. Vuelve a la página 1 apenas cambia la búsqueda o
     el filtro, para no quedar "varada" en una página que ya no existe. */
  const POR_PAGINA_FINANZAS = 10;
  const [paginaFinanzas, setPaginaFinanzas] = useState(1);
  useEffect(() => { setPaginaFinanzas(1); }, [buscarFinanzas, filtroPagoFinanzas]);
  const paginasFinanzas = Math.max(1, Math.ceil(finanzasFiltradas.length / POR_PAGINA_FINANZAS));
  const paginaFinanzasSegura = Math.min(paginaFinanzas, paginasFinanzas);
  const finanzasVisibles = finanzasFiltradas.slice(
    (paginaFinanzasSegura - 1) * POR_PAGINA_FINANZAS,
    paginaFinanzasSegura * POR_PAGINA_FINANZAS
  );

  /* ----------------------------------------------------------------
     COLA MANUAL DE PAGOS
     Solicitudes CONFIRMADA con comprobante de SINPE ya detectado — la
     misma cola que también se puede resolver con los botones del correo
     (esos sí con la ventana de gracia de unos minutos). Desde acá, en
     cambio, decidirPagoAdmin() aplica la decisión al instante: en
     cuanto responde, la fila ya no está CONFIRMADA (quedó PAGADA o
     PAGO_RECHAZADO), así que sale de la lista en vez de mostrar un chip de
     "en curso" — eso queda solo para las que alguien decidió por
     correo y siguen corriendo su propia ventana de gracia. Ver
     obtenerPagosPendientesAdmin/decidirPagoAdmin en apps-script.gs,
     sección 12.
  ---------------------------------------------------------------- */
  const [pagosPendientes, setPagosPendientes] = useState(null);   // null = cargando
  const [errorPagos, setErrorPagos] = useState("");
  const [decidiendoPago, setDecidiendoPago] = useState(null);   // tokenPago en curso | null

  useEffect(() => {
    if (!sesion) return;
    api.obtenerPagosPendientesAdmin(sesion)
      .then((r) => setPagosPendientes(r.pagos))
      .catch((err) => {
        if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
        setErrorPagos(err.message || "No pudimos cargar los pagos pendientes.");
        setPagosPendientes([]);
      });
  }, [sesion, salir]);

  const decidirPago = async (pago, accion) => {
    if (decidiendoPago) return;
    setDecidiendoPago(pago.tokenPago);
    setErrorPagos("");
    try {
      await api.decidirPagoAdmin(sesion, pago.tokenPago, accion);
      // Ya se aplicó (sala y correos enviados, o rechazo notificado): esta
      // fila deja de estar pendiente, no hay nada más que "en curso".
      setPagosPendientes((prev) => prev.filter((p) => p.tokenPago !== pago.tokenPago));
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      setErrorPagos(err.message || "No pudimos actualizar ese pago.");
    } finally {
      setDecidiendoPago(null);
    }
  };

  /* ----------------------------------------------------------------
     ROSTER COMPLETO Y CONTROL DE ESTADO (suspender / reactivar)
     Trae TODOS los cursos de TODOS los tutores, en cualquier estado, con
     su contacto privado — a diferencia del listado público de arriba.
     Sirve como base tanto del control de estado como del CSV de
     exportación. Ver listarRosterTutoresAdmin en apps-script.gs,
     sección 14.
  ---------------------------------------------------------------- */
  const [roster, setRoster] = useState(null);   // null = cargando
  const [errorRoster, setErrorRoster] = useState("");
  const [cambiandoEstado, setCambiandoEstado] = useState(null);   // idTutor en curso | null

  const cargarRoster = useCallback(async () => {
    try {
      const r = await api.obtenerRosterTutoresAdmin(sesion);
      setRoster(r.cursos);
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      setErrorRoster(err.message || "No pudimos cargar el listado de tutores.");
      setRoster([]);
    }
  }, [sesion, salir]);

  useEffect(() => { if (sesion) cargarRoster(); }, [sesion, cargarRoster]);

  const [bitacora, setBitacora] = useState(null);   // null = cargando
  const [errorBitacora, setErrorBitacora] = useState("");

  useEffect(() => {
    if (!sesion) return;
    api.obtenerBitacoraAdmin(sesion)
      .then((r) => setBitacora(r.bitacora))
      .catch((err) => {
        if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
        setErrorBitacora(err.message || "No pudimos cargar la bitácora de actividad.");
        setBitacora([]);
      });
  }, [sesion, salir]);

  /* ----------------------------------------------------------------
     INCIDENTES Y REPORTES
     Bandeja de solo lectura de los reportes que envían tutores y
     estudiantes — ver la sección 16 de apps-script.gs. El equipo no
     crea incidentes desde acá, solo los recibe y puede cerrar/reabrir
     cada reporte sin salir del panel.
  ---------------------------------------------------------------- */
  const [incidentes, setIncidentes] = useState(null);   // null = cargando
  const [errorIncidentes, setErrorIncidentes] = useState("");
  const [verIncidentesResueltos, setVerIncidentesResueltos] = useState(false);
  const [filtroOrigenIncidente, setFiltroOrigenIncidente] = useState("todas");   // "todas" | "estudiante" | "tutor"
  const [cambiandoIncidente, setCambiandoIncidente] = useState(null);   // id en curso | null

  const cargarIncidentes = useCallback(async () => {
    try {
      const r = await api.obtenerIncidentesAdmin(sesion);
      setIncidentes(r.incidentes);
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      setErrorIncidentes(err.message || "No pudimos cargar los incidentes.");
      setIncidentes([]);
    }
  }, [sesion, salir]);

  useEffect(() => { if (sesion) cargarIncidentes(); }, [sesion, cargarIncidentes]);

  const incidentesVisibles = useMemo(() => (incidentes || [])
    .filter((i) => verIncidentesResueltos || i.estado !== "resuelto")
    .filter((i) => filtroOrigenIncidente === "todas"
      || (i.origen || "Admin").toLowerCase() === filtroOrigenIncidente),
  [incidentes, verIncidentesResueltos, filtroOrigenIncidente]);
  const incidentesAbiertos = useMemo(() => (incidentes || [])
    .filter((i) => i.estado !== "resuelto").length,
  [incidentes]);

  const alternarEstadoIncidente = async (incidente) => {
    if (cambiandoIncidente) return;
    setCambiandoIncidente(incidente.id);
    setErrorIncidentes("");
    const nuevoEstado = incidente.estado === "resuelto" ? "abierto" : "resuelto";
    try {
      await api.cambiarEstadoIncidenteAdmin(sesion, incidente.id, nuevoEstado);
      setIncidentes((prev) => prev.map((i) => (i.id === incidente.id ? { ...i, estado: nuevoEstado } : i)));
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      setErrorIncidentes(err.message || "No pudimos actualizar ese incidente.");
    } finally {
      setCambiandoIncidente(null);
    }
  };

  const tutoresAgrupados = useMemo(() => {
    const porId = {};
    (roster || []).forEach((c) => {
      if (!porId[c.idTutor]) {
        porId[c.idTutor] = {
          idTutor: c.idTutor, nombre: c.nombre, carrera: c.carrera, anio: c.anio,
          correo: c.correo, telefono: c.telefono,
          cuentaTipo: c.cuentaTipo || "", cuentaNumero: c.cuentaNumero || "",
          cuentaTitular: c.cuentaTitular || "", cursos: [],
        };
      }
      porId[c.idTutor].cursos.push(c);
    });
    return Object.values(porId).sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [roster]);

  /* ----------------------------------------------------------------
     KPIs VISUALES DEL CICLO ACTIVO
     Sesiones y recaudado de ESTA semana (viernes a jueves en curso),
     a partir de sesionesFinancieras (ya viene sin agrupar, con su propio
     "semana" por fila — ver listarFinanzasAdmin más abajo). "Tutores
     activos" cuenta tutores con al menos un curso aprobado ahora mismo,
     del roster completo — no solo lo publicado en el buscador.
  ---------------------------------------------------------------- */
  const cicloActivoISO = useMemo(() => inicioSemanaISO(new Date()), []);
  const metricasSemana = useMemo(() => {
    const filas = (sesionesFinancieras || []).filter((s) => s.semana === cicloActivoISO);
    return {
      sesiones: filas.length,
      ingresos: filas.reduce((s, f) => s + (f.montoRecaudado || 0), 0),
      comision: filas.reduce((s, f) => s + (f.comision || 0), 0),
    };
  }, [sesionesFinancieras, cicloActivoISO]);
  const tutoresActivos = useMemo(() => (
    (roster || []).reduce((s, c) => (c.estado === "aprobado" ? s.add(c.idTutor) : s), new Set()).size
  ), [roster]);

  /* ----------------------------------------------------------------
     BÚSQUEDA Y FILTROS DE "TODOS LOS TUTORES"
     Por nombre/curso (texto libre) y por estado de la cuenta — sobre
     tutoresAgrupados, ya armado arriba. "Suspendido" mira si CUALQUIER
     curso quedó "suspendido" (mismo criterio que ya usaba el botón de
     reactivar); "Con pendientes" son tutores con algún curso todavía sin
     aprobar/rechazar.
  ---------------------------------------------------------------- */
  const [buscarTutores, setBuscarTutores] = useState("");
  const [filtroEstadoTutores, setFiltroEstadoTutores] = useState("todos");

  const tutoresFiltrados = useMemo(() => {
    const q = buscarTutores.trim().toLowerCase();
    return tutoresAgrupados.filter((t) => {
      if (filtroEstadoTutores === "suspendidos" && !t.cursos.some((c) => c.estado === "suspendido")) return false;
      if (filtroEstadoTutores === "activos" && !t.cursos.some((c) => c.estado === "aprobado")) return false;
      if (filtroEstadoTutores === "pendientes"
        && !t.cursos.some((c) => c.estado !== "aprobado" && c.estado !== "suspendido" && c.estado !== "rechazado" && c.estado !== "retirado")) return false;
      if (!q) return true;
      return t.nombre.toLowerCase().includes(q)
        || t.cursos.some((c) => c.cursoNombre.toLowerCase().includes(q));
    });
  }, [tutoresAgrupados, buscarTutores, filtroEstadoTutores]);

  const alternarEstadoTutor = async (tutorGrupo, accion) => {
    if (cambiandoEstado) return;
    if (accion === "suspender" && !window.confirm(
      `¿Suspender la cuenta de ${tutorGrupo.nombre}? Todos sus cursos aprobados se despublican de `
      + "inmediato y deja de recibir solicitudes nuevas hasta que lo reactive.")) return;

    setCambiandoEstado(tutorGrupo.idTutor);
    setErrorRoster("");
    try {
      if (accion === "suspender") await api.suspenderTutorAdmin(sesion, tutorGrupo.idTutor);
      else await api.reactivarTutorAdmin(sesion, tutorGrupo.idTutor);
      await cargarRoster();
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === "SESION_INVALIDA") { salir(); return; }
      setErrorRoster(err.message || "No pudimos actualizar el estado de ese tutor.");
    } finally {
      setCambiandoEstado(null);
    }
  };

  /* ----------------------------------------------------------------
     EXPORTACIÓN CSV
  ---------------------------------------------------------------- */
  const exportarRoster = () => {
    const csv = aCSV(roster || [], [
      { etiqueta: "ID tutor", valor: (r) => r.idTutor },
      { etiqueta: "Nombre", valor: (r) => r.nombre },
      { etiqueta: "Carrera", valor: (r) => r.carrera },
      { etiqueta: "Año", valor: (r) => r.anio },
      { etiqueta: "Curso", valor: (r) => r.cursoNombre },
      { etiqueta: "Estado", valor: (r) => r.estado },
      { etiqueta: "Publicado", valor: (r) => r.publicado },
      { etiqueta: "Correo", valor: (r) => r.correo },
      { etiqueta: "Teléfono", valor: (r) => r.telefono },
      { etiqueta: "Tipo de cuenta", valor: (r) => NOMBRE_TIPO_CUENTA[r.cuentaTipo] || r.cuentaTipo },
      { etiqueta: "Número de cuenta/IBAN", valor: (r) => r.cuentaNumero },
      { etiqueta: "A nombre de", valor: (r) => r.cuentaTitular },
      { etiqueta: "Tarifa base (₡/h)", valor: (r) => r.tarifaBase },
      { etiqueta: "Calificación", valor: (r) => (Number.isFinite(r.rating) ? r.rating.toFixed(2) : "") },
      { etiqueta: "Reseñas", valor: (r) => r.resenas },
      { etiqueta: "Sesiones completadas", valor: (r) => r.sesionesCompletadas },
      { etiqueta: "Registrado", valor: (r) => fecha(r.creado) },
    ]);
    descargarCSV(`ayudate-tutores-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const exportarSesiones = () => {
    const csv = aCSV(sesionesFinancieras || [], [
      { etiqueta: "Fecha de pago", valor: (s) => fecha(s.pagadoEn) },
      { etiqueta: "Ciclo (viernes)", valor: (s) => s.semana },
      { etiqueta: "Estado", valor: (s) => s.estado },
      { etiqueta: "Estudiante", valor: (s) => s.estudiante },
      { etiqueta: "Tutor", valor: (s) => s.tutorNombre },
      { etiqueta: "Curso", valor: (s) => s.curso },
      { etiqueta: "Horas", valor: (s) => s.horas },
      { etiqueta: "Recaudado (₡)", valor: (s) => s.montoRecaudado },
      { etiqueta: "Comisión Ayudate (₡)", valor: (s) => s.comision },
      { etiqueta: "Neto tutor (₡)", valor: (s) => s.montoTutor },
    ]);
    descargarCSV(`ayudate-finanzas-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const entrar = async () => {
    if (!password) { setErrorLogin("Ingrese la contraseña."); return; }
    setEntrando(true); setErrorLogin("");
    try {
      const r = await api.iniciarSesionAdmin(password);
      guardarSesionAdmin(r.token);
      setPassword("");
      setSesion(r.token);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (codigo === "CREDENCIALES_INVALIDAS") setErrorLogin("Contraseña incorrecta.");
      else if (codigo === "ADMIN_NO_CONFIGURADO") {
        setErrorLogin("El panel todavía no tiene una contraseña configurada del lado del servidor.");
      } else setErrorLogin(err.message || "No pudimos iniciar sesión.");
    } finally {
      setEntrando(false);
    }
  };

  const revisar = async (item, accion) => {
    if (procesando) return;
    setProcesando({ idTutor: item.idTutor, curso: item.curso, accion });
    setError("");
    try {
      if (accion === "aprobar") await api.aprobarCursoAdmin(sesion, item.idTutor, item.curso);
      else await api.rechazarCursoAdmin(sesion, item.idTutor, item.curso);
      setPendientes((prev) => prev.filter((p) => !(p.idTutor === item.idTutor && p.curso === item.curso)));
      mostrarExito(accion === "aprobar" ? `${item.cursoNombre} aprobado.` : `${item.cursoNombre} rechazado.`);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (codigo === "SESION_INVALIDA") { salir(); return; }
      if (codigo === "YA_REVISADO") setError("Ese curso ya se revisó, probablemente desde otra pestaña.");
      else setError(err.message || "No pudimos actualizar ese curso.");
    } finally {
      setProcesando(null);
    }
  };

  if (!sesion) {
    return (
      <div className="v2">
        <style>{ESTILOS_MINIMOS}</style>
        <div className="mini-wrap">
          <div className="logo" style={{ marginBottom: 28 }}><Marca /></div>
          <div className="mini-card">
            <h1>Administración</h1>
            <p>Ingrese la <b>contraseña del equipo</b> para revisar tutores y cursos pendientes.</p>

            <label className="lb" htmlFor="clave-admin">Contraseña</label>
            <input id="clave-admin" type="password" className="in" value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") entrar(); }} />

            {errorLogin && <div className="error" role="alert">{errorLogin}</div>}

            <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
              disabled={entrando} onClick={entrar}>
              {entrando ? "Entrando…" : "Entrar"}
            </button>
            <a className="btn" style={{ width: "100%", textAlign: "center", display: "block", marginTop: 10 }}
              href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
              Volver al inicio
            </a>
          </div>
        </div>
      </div>
    );
  }

  const tutoresNuevos = pendientes?.filter((p) => !p.esCursoNuevo) || [];
  const cursosAdicionales = pendientes?.filter((p) => p.esCursoNuevo) || [];

  const tarjeta = (item) => {
    const ocupado = procesando?.idTutor === item.idTutor && procesando?.curso === item.curso
      ? procesando.accion : null;
    const bloqueado = !!procesando;
    return (
      <div className="curso-card" key={`${item.idTutor}-${item.curso}`}>
        <div className="curso-card-titulo">
          <b>{item.nombre}</b>
          <span className="chip off">{item.cursoNombre}</span>
        </div>
        <p className="fila-tarifa">
          {item.carrera || "—"} · {item.anio || "—"} · Tarifa: {colones(precioDe(item.tarifaBase).cliente)}/h
          {item.creado ? ` · Enviado el ${fecha(item.creado)}` : ""}
        </p>
        <p className="curso-card-desc">{item.descripcion || "Sin descripción todavía."}</p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 28px", margin: "12px 0" }}>
          <div className="perfil-dato">
            <span className="perfil-dato-lbl">Correo</span>
            <span className="perfil-dato-val">{item.correo || "—"}</span>
          </div>
          <div className="perfil-dato">
            <span className="perfil-dato-lbl">Teléfono</span>
            <span className="perfil-dato-val">{item.telefono || "—"}</span>
          </div>
          <div className="perfil-dato">
            <span className="perfil-dato-lbl">Nota / nivel</span>
            <span className="perfil-dato-val">{item.nota || "—"}</span>
          </div>
          <div className="perfil-dato">
            <span className="perfil-dato-lbl">Comprobante</span>
            <span className="perfil-dato-val">
              {item.comprobante ? (
                <a href={item.comprobante} target="_blank" rel="noopener noreferrer">Ver comprobante</a>
              ) : "—"}
            </span>
          </div>
        </div>

        <div className="curso-card-acciones">
          <div className="curso-card-acciones-sec">
            <button className="btn btn-p" disabled={bloqueado} onClick={() => revisar(item, "aprobar")}>
              {ocupado === "aprobar" ? "Aprobando…" : "Aprobar"}
            </button>
            <button className="btn btn-danger" disabled={bloqueado} onClick={() => revisar(item, "rechazar")}>
              {ocupado === "rechazar" ? "Rechazando…" : "Rechazar"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`v2 ${clasesA11y(a11y)}`}>
      <style>{ESTILOS_MINIMOS}</style>
      <FiltrosColor />

      <header className="dash-topbar">
        <div className="dash-topbar-in dash-topbar-in-admin">
          <span className="logo"><Marca /></span>
          <div className="dash-topbar-user">
            <span>Panel de administración</span>
            <button className="btn" onClick={salir}>Cerrar sesión</button>
          </div>
        </div>
      </header>

      <nav className="dash-topbar-nav" aria-label="Accesos rápidos del panel">
        <div className="dash-topbar-nav-in">
          <a href="#nav-todos-tutores" onClick={(e) => irA(e, "nav-todos-tutores")}>Todos los tutores</a>
          <a href="#nav-bitacora" onClick={(e) => irA(e, "nav-bitacora")}>Bitácora de actividad</a>
          <a href="#nav-cursos-adicionales" onClick={(e) => irA(e, "nav-cursos-adicionales")}>
            Cursos adicionales
          </a>
          <a href="#nav-incidentes" onClick={(e) => irA(e, "nav-incidentes")}>
            Incidentes y reportes
            {incidentesAbiertos > 0 && (
              <span className="dash-topbar-badge" aria-label={`${incidentesAbiertos} incidentes abiertos`}>
                {incidentesAbiertos}
              </span>
            )}
          </a>
          <a href="#nav-exportar" onClick={(e) => irA(e, "nav-exportar")}>Exportar datos</a>
        </div>
      </nav>

      <div className="mini-wrap dash-wrap dash-wrap-admin">
        {error && <div className="error" role="alert" style={{ marginBottom: 20 }}>{error}</div>}
        {exito && <div className="exito" role="status" style={{ marginBottom: 20 }}>{exito}</div>}

        <div className="mini-card dash-sec">
          <h2>Métricas globales</h2>
          {tutoresPublicos === null ? (
            <p className="vacio">Cargando métricas…</p>
          ) : (
            <>
              <label className="lb" style={{ marginTop: 0 }}>Esta semana · viernes a jueves</label>
              <div className="stats-row">
                <div className="stat-card">
                  <span className="stat-num">{metricasSemana.sesiones}</span>
                  <span className="stat-lbl">Sesiones</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num">{colones(metricasSemana.ingresos)}</span>
                  <span className="stat-lbl">Recaudado</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num">{colones(metricasSemana.comision)}</span>
                  <span className="stat-lbl">Comisión de Ayudate</span>
                </div>
              </div>

              <label className="lb">Total acumulado</label>
              <div className="stats-row">
                <div className="stat-card">
                  <span className="stat-num">{tutoresActivos}</span>
                  <span className="stat-lbl">Tutores activos</span>
                  <span className="stat-sub">Con al menos <b>un curso aprobado</b></span>
                </div>
                <div className="stat-card">
                  <span className="stat-num">{metricas.totalSesiones}</span>
                  <span className="stat-lbl">Sesiones completadas</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num">{colones(metricas.ingresos)}</span>
                  <span className="stat-lbl">Ingresos de la plataforma</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num">{colones(metricas.comision)}</span>
                  <span className="stat-lbl">Comisión de Ayudate</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num" style={{ fontSize: 17, lineHeight: 1.3 }}>
                    {metricas.cursoTop ? infoCurso(metricas.cursoTop).nom : "—"}
                  </span>
                  <span className="stat-lbl">Curso más solicitado</span>
                  {metricas.cursoTop && (
                    <span className="stat-sub"><b>{metricas.sesionesTop}</b> sesiones</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mini-card dash-sec">
          <h2>Ranking de tutores</h2>
          <div style={{ display: "flex", gap: "12px 16px", flexWrap: "wrap", marginBottom: 6 }}>
            <div style={{ flex: "1 1 200px" }}>
              <label className="lb" htmlFor="filtro-area">Filtrar por área</label>
              <select id="filtro-area" className="in" value={filtroArea}
                onChange={(e) => setFiltroArea(e.target.value)}>
                <option value="todas">Todas las áreas</option>
                {areasPresentes.map((a) => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label className="lb" htmlFor="filtro-curso">Filtrar por curso</label>
              <select id="filtro-curso" className="in" value={filtroCurso}
                onChange={(e) => setFiltroCurso(e.target.value)}>
                <option value="todos">Todos los cursos</option>
                {cursosPresentes.map((c) => (
                  <option key={c.curso} value={c.curso}>{c.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {tutoresPublicos === null ? (
            <p className="vacio">Cargando ranking…</p>
          ) : !ranking.length ? (
            <p className="vacio">
              {ofertas.length ? "No hay tutores para ese filtro." : "Todavía no hay cursos publicados."}
            </p>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="rank-tabla tabla-ranking">
                  <thead>
                    <tr>
                      <th>#</th><th>Tutor</th><th>Curso</th><th>Sesiones</th><th>Calificación</th>
                      <th>Puntualidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankingVisible.map((o, i) => {
                      const posicion = (paginaRankingSegura - 1) * POR_PAGINA_RANKING + i + 1;
                      return (
                      <tr key={o.ofertaId}>
                        <td className={`rank-pos ${posicion <= 3 ? "top3" : ""}`}>
                          {posicion}
                        </td>
                        <td>
                          <button type="button" className="tutor-link"
                            onClick={() => setTutorSeleccionado(o.id)}>
                            {o.nom}
                          </button>
                        </td>
                        <td><span className="chip off">{infoCurso(o.curso).nom}</span></td>
                        <td>{o.sesiones}</td>
                        <td className="rank-rating">
                          {Number.isFinite(o.rating)
                            ? <><b>★ {o.rating.toFixed(1)}</b> ({o.resenas || 0})</>
                            : "Sin calificar"}
                        </td>
                        <td className="rank-rating">
                          {textoPuntualidad(o.id) || "Sin datos"}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {paginasRanking > 1 && (
                <nav className="pag" aria-label="Páginas del ranking de tutores">
                  <button type="button" className="pbtn" disabled={paginaRankingSegura === 1}
                    onClick={() => setPaginaRanking(paginaRankingSegura - 1)}
                    aria-label="Página anterior">‹</button>

                  {Array.from({ length: paginasRanking }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === paginasRanking
                      || Math.abs(p - paginaRankingSegura) <= 1)
                    .reduce((acc, p, i, arr) => {
                      if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, i) => p === "…"
                      ? <span className="psalto" key={`s${i}`} aria-hidden="true">…</span>
                      : (
                        <button type="button" key={p}
                          className={`pbtn ${p === paginaRankingSegura ? "on" : ""}`}
                          aria-current={p === paginaRankingSegura ? "page" : undefined}
                          aria-label={`Página ${p}`}
                          onClick={() => setPaginaRanking(p)}>{p}</button>
                      ))}

                  <button type="button" className="pbtn"
                    disabled={paginaRankingSegura === paginasRanking}
                    onClick={() => setPaginaRanking(paginaRankingSegura + 1)}
                    aria-label="Página siguiente">›</button>
                </nav>
              )}
            </>
          )}
        </div>

        <div className="mini-card dash-sec dash-sec-finanzas">
          <h2>Finanzas y liquidaciones de tutores</h2>
          {errorFinanzas && <div className="error" role="alert" style={{ marginBottom: 14 }}>{errorFinanzas}</div>}
          {finanzas === null ? (
            <p className="vacio">Cargando finanzas…</p>
          ) : !finanzas.length ? (
            <p className="vacio">Todavía no hay sesiones pagadas para mostrar.</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: "12px 16px", flexWrap: "wrap", marginBottom: 16, textAlign: "left" }}>
                <div style={{ flex: "2 1 220px" }}>
                  <label className="lb" htmlFor="buscar-finanzas">Buscar tutor</label>
                  <input id="buscar-finanzas" type="search" autoComplete="off" className="in"
                    value={buscarFinanzas} placeholder="Nombre del tutor…"
                    onChange={(e) => setBuscarFinanzas(e.target.value)} />
                </div>
                <div style={{ flex: "1 1 180px" }}>
                  <label className="lb" htmlFor="filtro-pago">Estado de pago</label>
                  <select id="filtro-pago" className="in" value={filtroPagoFinanzas}
                    onChange={(e) => setFiltroPagoFinanzas(e.target.value)}>
                    <option value="todos">Todos</option>
                    <option value="pendientes">Pendientes de pagar</option>
                    <option value="pagados">Ya pagados</option>
                  </select>
                </div>
              </div>
              {!finanzasFiltradas.length ? (
                <p className="vacio">Ningún ciclo coincide con esos filtros.</p>
              ) : (
            <>
            <div style={{ overflowX: "auto" }}>
              <table className="rank-tabla tabla-finanzas">
                <thead>
                  <tr>
                    <th>Ciclo (viernes a jueves)</th><th>Tutor</th><th>Sesiones</th>
                    <th>Recaudado (SINPE)</th><th>Comisión Ayudate</th><th>Neto tutor</th>
                    <th>Cuenta bancaria del tutor</th><th>Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {finanzasVisibles.map((f) => {
                    const clave = `${f.semana}::${f.tutorId}`;
                    return (
                      <tr key={clave}>
                        <td>{rangoSemana(f.semana)}</td>
                        <td><b>{f.tutorNombre}</b></td>
                        <td>{f.sesiones}</td>
                        <td>{colones(f.montoRecaudado)}</td>
                        <td>{colones(f.comision)}</td>
                        <td><b>{colones(f.montoTutor)}</b></td>
                        <td>
                          {f.cuentaNumero ? (
                            <>
                              <b>{NOMBRE_TIPO_CUENTA[f.cuentaTipo] || f.cuentaTipo}</b> · {f.cuentaNumero}
                              {f.cuentaTitular && (
                                <>
                                  <br />
                                  <span style={{ color: "var(--text-3)", fontSize: 13 }}>
                                    A nombre de {f.cuentaTitular}
                                  </span>
                                </>
                              )}
                            </>
                          ) : (
                            <span style={{ color: "var(--text-3)" }}>No registrada</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                            <button type="button"
                              className={`pago-btn ${f.liquidado ? "pagado" : "pendiente"}`}
                              disabled={liquidando === clave}
                              title={f.liquidado
                                ? "Ya pagado: haga clic para deshacer y corregir un error"
                                : "Requiere subir el comprobante de la transferencia"}
                              onClick={() => (f.liquidado ? alternarLiquidacion(f) : setPagoParaComprobante(f))}>
                              {liquidando === clave ? "Guardando…" : f.liquidado ? "✓ Pagado" : "Marcar como pagado"}
                            </button>
                            {f.liquidado && f.comprobante && (
                              <a href={f.comprobante} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: 13 }}>
                                Ver comprobante
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {paginasFinanzas > 1 && (
              <nav className="pag" aria-label="Páginas de finanzas y liquidaciones">
                <button type="button" className="pbtn" disabled={paginaFinanzasSegura === 1}
                  onClick={() => setPaginaFinanzas(paginaFinanzasSegura - 1)}
                  aria-label="Página anterior">‹</button>

                {Array.from({ length: paginasFinanzas }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === paginasFinanzas
                    || Math.abs(p - paginaFinanzasSegura) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === "…"
                    ? <span className="psalto" key={`s${i}`} aria-hidden="true">…</span>
                    : (
                      <button type="button" key={p}
                        className={`pbtn ${p === paginaFinanzasSegura ? "on" : ""}`}
                        aria-current={p === paginaFinanzasSegura ? "page" : undefined}
                        aria-label={`Página ${p}`}
                        onClick={() => setPaginaFinanzas(p)}>{p}</button>
                    ))}

                <button type="button" className="pbtn"
                  disabled={paginaFinanzasSegura === paginasFinanzas}
                  onClick={() => setPaginaFinanzas(paginaFinanzasSegura + 1)}
                  aria-label="Página siguiente">›</button>
              </nav>
            )}
            </>
              )}
            </>
          )}
        </div>

        <div className="mini-card dash-sec">
          <h2>Pagos de estudiantes pendientes de verificar</h2>
          {errorPagos && <div className="error" role="alert" style={{ marginBottom: 14 }}>{errorPagos}</div>}
          {pagosPendientes === null ? (
            <p className="vacio">Cargando pagos…</p>
          ) : !pagosPendientes.length ? (
            <p className="vacio">No hay comprobantes esperando revisión.</p>
          ) : pagosPendientes.map((p) => {
            const ocupado = decidiendoPago === p.tokenPago;
            return (
              <div className="curso-card" key={p.tokenPago}>
                <div className="curso-card-titulo">
                  <b>{p.estudiante}</b>
                  <span className="chip off">{p.curso}</span>
                  {p.decision && (
                    <span className={`chip ${p.decision === "APROBAR" ? "success" : "danger"}`}>
                      {p.decision === "APROBAR" ? "Aprobación en curso" : "Rechazo en curso"}
                    </span>
                  )}
                </div>
                <p className="fila-tarifa">
                  Con {p.tutorNombre} · {p.horas} h · {colones(p.montoTotal)}
                  {p.comprobanteRecibido ? ` · Comprobante recibido el ${fecha(p.comprobanteRecibido)}` : ""}
                </p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 28px", margin: "12px 0" }}>
                  <div className="perfil-dato">
                    <span className="perfil-dato-lbl">Correo</span>
                    <span className="perfil-dato-val">{p.correoEst || "—"}</span>
                  </div>
                  <div className="perfil-dato">
                    <span className="perfil-dato-lbl">Teléfono</span>
                    <span className="perfil-dato-val">{p.telEst || "—"}</span>
                  </div>
                </div>

                {/* El comprobante ahora lo sube el estudiante en /pagar y queda como
                    archivo en Drive (ver subirComprobantePago en apps-script.gs). Los
                    pocos que entren por el camino de rescate —respondiendo el correo de
                    cobro— llegan sin enlace, y esos siguen viviendo en Gmail. */}
                {p.comprobante ? (
                  <p className="fila-tarifa">
                    <a href={p.comprobante} target="_blank" rel="noopener noreferrer">
                      Ver el comprobante
                    </a>{" "}
                    antes de decidir.
                  </p>
                ) : (
                  <p className="fila-tarifa">
                    Este llegó como respuesta al correo de cobro: revise el
                    <b> comprobante adjunto</b> en el hilo de Gmail antes de decidir.
                  </p>
                )}

                <div className="curso-card-acciones">
                  <div className="curso-card-acciones-sec">
                    <button className="btn btn-p" disabled={ocupado} onClick={() => decidirPago(p, "aprobar")}>
                      {ocupado ? "Guardando…" : "Aprobar pago"}
                    </button>
                    <button className="btn btn-danger" disabled={ocupado} onClick={() => decidirPago(p, "rechazar")}>
                      {ocupado ? "Guardando…" : "Rechazar pago"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mini-card dash-sec" id="nav-todos-tutores">
          <h2>Todos los tutores</h2>
          {errorRoster && <div className="error" role="alert" style={{ marginBottom: 14 }}>{errorRoster}</div>}
          {roster === null ? (
            <p className="vacio">Cargando tutores…</p>
          ) : !tutoresAgrupados.length ? (
            <p className="vacio">Todavía no hay tutores registrados.</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: "12px 16px", flexWrap: "wrap", marginBottom: 18 }}>
                <div style={{ flex: "2 1 240px" }}>
                  <label className="lb" htmlFor="buscar-tutores">Buscar</label>
                  <input id="buscar-tutores" type="search" autoComplete="off" className="in"
                    value={buscarTutores} placeholder="Nombre del tutor o curso…"
                    onChange={(e) => setBuscarTutores(e.target.value)} />
                </div>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="lb" htmlFor="filtro-estado-tutores">Estado de la cuenta</label>
                  <select id="filtro-estado-tutores" className="in" value={filtroEstadoTutores}
                    onChange={(e) => setFiltroEstadoTutores(e.target.value)}>
                    <option value="todos">Todos</option>
                    <option value="activos">Con curso aprobado</option>
                    <option value="pendientes">Con curso pendiente</option>
                    <option value="suspendidos">Suspendidos</option>
                  </select>
                </div>
              </div>

              {!tutoresFiltrados.length ? (
                <p className="vacio">Ningún tutor coincide con esos filtros.</p>
              ) : (
                <div className="tutores-grid">
                  {tutoresFiltrados.map((t) => {
                    const tieneAprobados = t.cursos.some((c) => c.estado === "aprobado");
                    const tieneSuspendidos = t.cursos.some((c) => c.estado === "suspendido");
                    const ocupado = cambiandoEstado === t.idTutor;
                    return (
                      <div className="curso-card" key={t.idTutor}>
                        <div className="curso-card-titulo">
                          <b>{t.nombre}</b>
                          {tieneSuspendidos && <span className="chip off">Suspendido</span>}
                        </div>
                        <p className="fila-tarifa">
                          {t.carrera || "—"} · {t.anio || "—"}
                        </p>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 22px", margin: "12px 0" }}>
                          <div className="perfil-dato">
                            <span className="perfil-dato-lbl">Correo</span>
                            <span className="perfil-dato-val">{t.correo || "—"}</span>
                          </div>
                          <div className="perfil-dato">
                            <span className="perfil-dato-lbl">Teléfono</span>
                            <span className="perfil-dato-val">{t.telefono || "—"}</span>
                          </div>
                          <div className="perfil-dato">
                            <span className="perfil-dato-lbl">Cuenta bancaria</span>
                            <span className="perfil-dato-val">
                              {t.cuentaNumero
                                ? `${NOMBRE_TIPO_CUENTA[t.cuentaTipo] || t.cuentaTipo} · ${t.cuentaNumero}`
                                : <span style={{ color: "var(--text-3)" }}>No registrada</span>}
                            </span>
                          </div>
                          <div className="perfil-dato">
                            <span className="perfil-dato-lbl">A nombre de</span>
                            <span className="perfil-dato-val">{t.cuentaTitular || "—"}</span>
                          </div>
                        </div>

                        <span className="lb" style={{ margin: "4px 0 8px" }}>Cursos</span>
                        {t.cursos.map((c) => (
                          <div className="fila" key={c.curso} style={{ padding: "10px 0" }}>
                            <div className="fila-info">
                              <b>{c.cursoNombre}</b>{" "}
                              <span className={`chip ${
                                c.estado === "aprobado" ? (c.publicado === "SI" ? "on" : "off")
                                : c.estado === "suspendido" ? "off"
                                : c.estado === "rechazado" ? "danger" : "warn"
                              }`}>
                                {c.estado === "aprobado" ? (c.publicado === "SI" ? "Publicado" : "Oculto")
                                  : c.estado === "suspendido" ? "Suspendido"
                                  : c.estado === "rechazado" ? "No aprobado" : "En revisión"}
                              </span>
                              <p>
                                {c.sesionesCompletadas} {c.sesionesCompletadas === 1 ? "sesión" : "sesiones"} ·{" "}
                                {Number.isFinite(c.rating)
                                  ? `★ ${c.rating.toFixed(1)} (${c.resenas || 0})`
                                  : "Sin calificar"}
                                {" · "}{colones(c.tarifaBase)}/h
                              </p>
                            </div>
                          </div>
                        ))}

                        <div className="curso-card-acciones">
                          <div className="curso-card-acciones-sec">
                            {tieneAprobados && (
                              <button className="btn-peligro-ghost" disabled={ocupado}
                                onClick={() => alternarEstadoTutor(t, "suspender")}>
                                {ocupado ? "…" : "Suspender cuenta"}
                              </button>
                            )}
                            {tieneSuspendidos && (
                              <button className="btn-ghost" disabled={ocupado}
                                onClick={() => alternarEstadoTutor(t, "reactivar")}>
                                {ocupado ? "…" : "Reactivar cuenta"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="mini-card dash-sec" id="nav-incidentes">
          <div className="displb">
            <h2 style={{ margin: 0 }}>
              Incidentes y reportes
              {incidentesAbiertos > 0 && <span className="chip warn" style={{ marginLeft: 10 }}>
                {incidentesAbiertos} abierto{incidentesAbiertos === 1 ? "" : "s"}
              </span>}
            </h2>
          </div>
          <p className="fila-tarifa" style={{ marginBottom: 16 }}>
            Bandeja de solo lectura con las quejas, ausencias o problemas técnicos que reportan
            tutores y estudiantes, para darles seguimiento sin perderlos entre correos o WhatsApp
            sueltos.
          </p>
          {errorIncidentes && <div className="error" role="alert" style={{ marginBottom: 14 }}>{errorIncidentes}</div>}

          <div className="displb">
            <label className="lb" style={{ margin: 0 }}>
              {incidentesVisibles.length} incidente{incidentesVisibles.length === 1 ? "" : "s"}
            </label>
            <button type="button" className="limpiar" onClick={() => setVerIncidentesResueltos((v) => !v)}>
              {verIncidentesResueltos ? "Ocultar resueltos" : "Ver resueltos también"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}
            role="group" aria-label="Filtrar incidentes por quién los reportó">
            {[
              { id: "todas", label: "Todas" },
              { id: "estudiante", label: "Enviados por estudiantes" },
              { id: "tutor", label: "Enviados por tutores" },
            ].map((f) => (
              <button key={f.id} type="button"
                className={`chip ${filtroOrigenIncidente === f.id ? "on" : "off"}`}
                style={{ border: "none", cursor: "pointer" }}
                aria-pressed={filtroOrigenIncidente === f.id}
                onClick={() => setFiltroOrigenIncidente(f.id)}>
                {f.label}
              </button>
            ))}
          </div>

          {incidentes === null ? (
            <p className="vacio">Cargando incidentes…</p>
          ) : !incidentesVisibles.length ? (
            <p className="vacio">
              {filtroOrigenIncidente !== "todas"
                ? "Ningún incidente coincide con ese filtro."
                : verIncidentesResueltos ? "Todavía no hay incidentes reportados." : "No hay incidentes abiertos."}
            </p>
          ) : incidentesVisibles.map((inc) => {
            const ocupado = cambiandoIncidente === inc.id;
            const TIPO_LB = { queja: "Queja de estudiante", ausencia: "Ausencia del tutor",
              tecnico: "Problema técnico", otro: "Otro" };
            return (
              <div className="fila" key={inc.id}>
                <div className="fila-info">
                  <b>{TIPO_LB[inc.tipo] || inc.tipo}</b>{" "}
                  <span className={`chip ${inc.estado === "resuelto" ? "success" : "warn"}`}>
                    {inc.estado === "resuelto" ? "Resuelto" : "Abierto"}
                  </span>{" "}
                  <span className="chip off">Reportado por: {inc.origen || "Admin"}</span>
                  <p>
                    {[inc.tutorNombre, inc.curso ? infoCurso(inc.curso).nom : "", inc.estudiante]
                      .filter(Boolean).join(" · ") || "Sin datos de contexto"}
                    {" · "}{fecha(inc.fecha)}
                  </p>
                  <p className="fila-nota">{inc.descripcion}</p>
                </div>
                <div className="fila-acciones">
                  <button className={inc.estado === "resuelto" ? "btn-ghost" : "btn btn-p"} disabled={ocupado}
                    onClick={() => alternarEstadoIncidente(inc)}>
                    {ocupado ? "…" : inc.estado === "resuelto" ? "Reabrir" : "Marcar resuelto"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mini-card dash-sec" id="nav-bitacora">
          <h2>Bitácora de actividad</h2>
          {errorBitacora && <div className="error" role="alert" style={{ marginBottom: 14 }}>{errorBitacora}</div>}
          {bitacora === null ? (
            <p className="vacio">Cargando bitácora…</p>
          ) : !bitacora.length ? (
            <p className="vacio">Todavía no hay acciones registradas.</p>
          ) : bitacora.map((e, i) => (
            <div className="fila" key={i}>
              <div className="fila-info">
                <b>{e.accion}</b>
                <p>
                  {e.detalle} · {fecha(e.fecha)}{" "}
                  {new Date(e.fecha).toLocaleTimeString("es-CR", { hour: "numeric", minute: "2-digit" })}
                </p>
              </div>
            </div>
          ))}
        </div>

        {cargando ? (
          <div className="mini-card"><p style={{ margin: 0 }}>Cargando cola de aprobación…</p></div>
        ) : (
          <>
            <div className="mini-card dash-sec">
              <h2>Tutores nuevos</h2>
              {!tutoresNuevos.length ? (
                <p className="vacio">No hay tutores nuevos esperando aprobación.</p>
              ) : tutoresNuevos.map(tarjeta)}
            </div>

            <div className="mini-card dash-sec" id="nav-cursos-adicionales">
              <h2>Cursos adicionales</h2>
              {!cursosAdicionales.length ? (
                <p className="vacio">No hay cursos adicionales de tutores ya registrados esperando aprobación.</p>
              ) : cursosAdicionales.map(tarjeta)}
            </div>
          </>
        )}

        <div className="mini-card dash-sec" id="nav-exportar">
          <h2>Exportar datos</h2>
          <p className="fila-tarifa" style={{ marginBottom: 14 }}>
            Descarga en <b>CSV</b>, lista para abrir en Excel o Google Sheets.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" disabled={!roster || !roster.length} onClick={exportarRoster}>
              Exportar tutores (CSV)
            </button>
            <button className="btn" disabled={!sesionesFinancieras || !sesionesFinancieras.length}
              onClick={exportarSesiones}>
              Exportar sesiones y finanzas (CSV)
            </button>
          </div>
        </div>
      </div>

      {perfilTutor && (
        <div className="ov" onClick={() => setTutorSeleccionado(null)}>
          <div className="ov-caja">
            <div className="mdl mdl-ancho" role="dialog" aria-modal="true"
              aria-label={`Ficha de ${perfilTutor.nombre}`} onClick={(e) => e.stopPropagation()}>
              <div className="mdl-h">
                <h3>{perfilTutor.nombre}</h3>
                <button className="x" onClick={() => setTutorSeleccionado(null)}
                  aria-label="Cerrar ficha del tutor">×</button>
              </div>
              <div className="mdl-b">
                <p className="fila-tarifa">{perfilTutor.carrera || "—"} · {perfilTutor.anio || "—"}</p>

                <div className="stats-row" style={{ marginTop: 16, marginBottom: 22 }}>
                  <div className="stat-card">
                    <span className="stat-num">{perfilTutor.totalSesiones}</span>
                    <span className="stat-lbl">Sesiones completadas</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-num">{colones(perfilTutor.ingresos)}</span>
                    <span className="stat-lbl">Ingresos generados</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-num">{colones(perfilTutor.ganancias)}</span>
                    <span className="stat-lbl">Ganancias del tutor</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-num">
                      {perfilTutor.puntualidad?.total ? `${perfilTutor.puntualidad.porcentaje}%` : "—"}
                    </span>
                    <span className="stat-lbl">Puntualidad</span>
                    {perfilTutor.puntualidad?.total > 0 && (
                      <span className="stat-sub">
                        {perfilTutor.puntualidad.tardias === 1
                          ? "1 tardía registrada"
                          : `${perfilTutor.puntualidad.tardias} tardías registradas`}
                      </span>
                    )}
                  </div>
                </div>

                <span className="lb" style={{ margin: "0 0 8px" }}>Cursos ofrecidos</span>
                {perfilTutor.cursos.map((c) => (
                  <div className="fila" key={c.curso}>
                    <div className="fila-info">
                      <b>{infoCurso(c.curso).nom}</b>
                      <p>
                        {c.sesiones} {c.sesiones === 1 ? "sesión" : "sesiones"} ·{" "}
                        {Number.isFinite(c.rating)
                          ? `★ ${c.rating.toFixed(1)} (${c.resenas || 0} reseña${c.resenas === 1 ? "" : "s"})`
                          : "Sin calificar todavía"}
                        {" · "}{colones(c.tarifaCliente)}/h
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {pagoParaComprobante && (
        <ModalComprobantePago
          fila={pagoParaComprobante}
          onCerrar={() => setPagoParaComprobante(null)}
          onConfirmar={marcarComoPagado}
        />
      )}

      <Accesibilidad ajustes={a11y} setAjustes={setA11y} />
    </div>
  );
}

/* Exige subir la imagen del comprobante ANTES de poder marcar un ciclo
   como PAGADO: el botón de confirmar queda deshabilitado hasta que haya
   un archivo preparado. El servidor vuelve a exigirlo de todas formas
   (ver marcarLiquidacionAdmin en apps-script.gs) — esto es solo para que
   el admin no llegue hasta ahí sin el archivo en mano. Al confirmar, el
   mismo comprobante se sube a Drive Y se adjunta al correo que le llega
   al tutor (ver avisarPagoLiquidadoTutor_), así que esta es la única
   oportunidad de elegirlo. */
function ModalComprobantePago({ fila, onCerrar, onConfirmar }) {
  const [comprobante, setComprobante] = useState(null);
  const [preparando, setPreparando] = useState(false);
  const [errorArchivo, setErrorArchivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const tokenRef = useRef(0);

  const elegirArchivo = async (archivo) => {
    try {
      validarExtensionImagen(archivo);
    } catch (err) {
      setErrorArchivo(err.message);
      return;
    }
    const miToken = ++tokenRef.current;
    setPreparando(true); setErrorArchivo("");
    try {
      const lista = await prepararComprobante(archivo);
      if (tokenRef.current === miToken) setComprobante(lista);
    } catch (err) {
      if (tokenRef.current === miToken) setErrorArchivo(err.message);
    } finally {
      if (tokenRef.current === miToken) setPreparando(false);
    }
  };

  const confirmar = async () => {
    if (!comprobante) { setError("Suba la imagen del comprobante antes de confirmar."); return; }
    setGuardando(true); setError("");
    try {
      await onConfirmar(fila, comprobante);
    } catch (err) {
      setError(err.message || "No pudimos guardar el pago. Pruebe de nuevo.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Capa onCerrar={onCerrar} etiqueta={`Comprobante de pago de ${fila.tutorNombre}`}
      confirmarSalida={!!comprobante}>
      <div className="mdl">
        <div className="mdl-h">
          <h3>Marcar como pagado</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrar}>×</button>
        </div>
        <div className="mdl-b">
          <p className="fila-tarifa" style={{ marginTop: 0 }}>
            <b>{fila.tutorNombre}</b> · Ciclo {rangoSemana(fila.semana)} · {colones(fila.montoTutor)}
          </p>
          <p style={{ fontSize: 15, color: "var(--text-2)", lineHeight: 1.6, marginBottom: 16 }}>
            Suba la captura o foto del comprobante de la transferencia. Se guarda en la carpeta de
            Drive del tutor y se le adjunta al correo que le confirma el pago.
          </p>

          <label className="lb" style={{ marginTop: 0 }}>Comprobante de la transferencia</label>
          <AlertaArchivo mensaje={errorArchivo} />
          <div className="fotorow">
            <div className="fotoprev">
              {comprobante ? <img src={comprobante} alt="Vista previa del comprobante" /> : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
                    fill="none" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              )}
            </div>
            <div className="fotoacc">
              <label className={`btn fotobtn ${preparando ? "ocupado" : ""}`}>
                {preparando ? "Preparando…" : comprobante ? "Cambiar" : "Subir comprobante"}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) elegirArchivo(f);
                }} />
              </label>
              <p className="ayuda" style={{ margin: "8px 0 0" }}>
                Formatos permitidos: <b>{FORMATOS_IMAGEN_TEXTO}</b>.
              </p>
            </div>
          </div>

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando || !comprobante} onClick={confirmar}>
            {guardando ? "Guardando…" : "Confirmar pago y avisar al tutor"}
          </button>
        </div>
      </div>
    </Capa>
  );
}
